import { ROUTES } from '../../constants/routes';

export const FOUNDERS_TO_MEMBER_APP_HOST = {
  'founders.starthub.academy': 'www.starthub.academy',
  // Helm FRONTEND_URL uses www.dev / www.staging; bare hosts remain reverse-lookup aliases.
  'founders.dev.starthub.academy': 'www.dev.starthub.academy',
  'founders.staging.starthub.academy': 'www.staging.starthub.academy',
} as const;

export type FoundersHostname = keyof typeof FOUNDERS_TO_MEMBER_APP_HOST;

function isOwnFoundersHostname(hostname: string): hostname is FoundersHostname {
  return Object.hasOwn(FOUNDERS_TO_MEMBER_APP_HOST, hostname);
}

export function getMemberAppHostname(hostname: string): string | undefined {
  if (!isOwnFoundersHostname(hostname)) {
    return undefined;
  }
  return FOUNDERS_TO_MEMBER_APP_HOST[hostname];
}

export function getFoundersHostnameForMemberApp(memberAppHostname: string): string | undefined {
  for (const [foundersHostname, mappedMemberHostname] of Object.entries(FOUNDERS_TO_MEMBER_APP_HOST)) {
    if (mappedMemberHostname === memberAppHostname) {
      return foundersHostname;
    }
    // Accept bare env hosts (dev.starthub.academy) when the canonical map value is www.*.
    if (mappedMemberHostname.startsWith('www.') && mappedMemberHostname.slice(4) === memberAppHostname) {
      return foundersHostname;
    }
  }
  return undefined;
}

export function isFoundersHostname(hostname: string): hostname is FoundersHostname {
  return isOwnFoundersHostname(hostname);
}

export function buildFoundersOrigin(hostname: string): string | undefined {
  if (!isFoundersHostname(hostname)) {
    return undefined;
  }
  return `https://${hostname}`;
}

export function isLandingOnlyReturnPath(path: string): boolean {
  const pathname = path.split('?')[0]?.split('#')[0] ?? path;
  return pathname === ROUTES.GOODIE_BAG || pathname.startsWith(`${ROUTES.GOODIE_BAG}/`);
}

export function isAllowlistedFoundersOrigin(origin: string | null | undefined): origin is string {
  if (!origin) {
    return false;
  }

  try {
    const url = new URL(origin);
    // Exact canonical https://<founders-host> only — no port, path, query, or hash.
    if (url.protocol !== 'https:' || url.port !== '' || url.search !== '' || url.hash !== '') {
      return false;
    }
    if (!(url.pathname === '/' || url.pathname === '')) {
      return false;
    }
    const canonical = buildFoundersOrigin(url.hostname);
    return canonical !== undefined && (origin === canonical || origin === `${canonical}/`);
  } catch {
    return false;
  }
}

export function isSafeRelativeReturnPath(path: string): boolean {
  if (!(path.startsWith('/') && !path.startsWith('//') && !path.includes('\\'))) {
    return false;
  }

  const firstPrintableCharacterCode = 32;
  const deleteCharacterCode = 127;
  return !Array.from(path).some(character => {
    const characterCode = character.charCodeAt(0);
    return characterCode < firstPrintableCharacterCode || characterCode === deleteCharacterCode;
  });
}

export interface ResolveCrossAppReturnHrefOptions {
  returnOrigin?: string | null;
  memberAppHostname?: string;
}

/**
 * Resolves pricing/checkout "back" targets that originate on founders landing
 * hosts but execute on the member app (relative paths like /goodie-bag/...).
 */
export function resolveCrossAppReturnHref(returnPath: string, options: ResolveCrossAppReturnHrefOptions = {}): string {
  if (!(isSafeRelativeReturnPath(returnPath) && isLandingOnlyReturnPath(returnPath))) {
    return returnPath;
  }

  if (options.returnOrigin && isAllowlistedFoundersOrigin(options.returnOrigin)) {
    const foundersOrigin = buildFoundersOrigin(new URL(options.returnOrigin).hostname);
    if (foundersOrigin) {
      return `${foundersOrigin}${returnPath}`;
    }
  }

  if (options.memberAppHostname) {
    const foundersHost = getFoundersHostnameForMemberApp(options.memberAppHostname);
    if (foundersHost) {
      return `https://${foundersHost}${returnPath}`;
    }
  }

  return returnPath;
}

export interface ContentLockReturnTarget {
  returnTo: string;
  returnOrigin?: string;
}

export function buildContentLockReturnTarget(
  pathname: string,
  search = '',
  hostname?: string,
): ContentLockReturnTarget {
  const returnTo = `${pathname}${search}`;
  if (!(hostname && isLandingOnlyReturnPath(pathname))) {
    return { returnTo };
  }

  const returnOrigin = buildFoundersOrigin(hostname);
  if (!returnOrigin) {
    return { returnTo };
  }

  return { returnTo, returnOrigin };
}
